import React, { createContext, ReactElement, useMemo } from 'react';
import useFiscalPeriods from './useFiscalPeriods';
import useFiscalQuarters from './useFiscalQuarters';
import useFiscalYears from './useFiscalYears';
import getCurrentFiscalEntity from './getCurrentFiscalEntity';

interface ContextType {
  fiscalPeriods: FixedPeriods.FiscalPeriod[];
  fiscalQuarters: FixedPeriods.FiscalQuarter[];
  fiscalYears: FixedPeriods.FiscalYear[];
  currentFiscalPeriod: FixedPeriods.FiscalPeriod | undefined;
  currentFiscalQuarter: FixedPeriods.FiscalQuarter | undefined;
  currentFiscalYear: FixedPeriods.FiscalYear | undefined;
  allFiscals: FixedPeriods.AllFiscals | undefined;
}

const FiscalPeriodsContext = createContext<ContextType>({
  fiscalPeriods: [],
  fiscalQuarters: [],
  fiscalYears: [],
  currentFiscalPeriod: undefined,
  currentFiscalQuarter: undefined,
  currentFiscalYear: undefined,
  allFiscals: undefined,
});

const FiscalPeriodsProvider = ({ children }: { children: ReactElement }) => {
  const { fiscalPeriods } = useFiscalPeriods();
  const { fiscalQuarters } = useFiscalQuarters();
  const { fiscalYears } = useFiscalYears();

  const currentFiscalPeriod = useMemo(
    () =>
      getCurrentFiscalEntity(fiscalPeriods) as
        | FixedPeriods.FiscalPeriod
        | undefined,
    [fiscalPeriods],
  );

  const currentFiscalQuarter = useMemo(
    () =>
      getCurrentFiscalEntity(fiscalQuarters) as
        | FixedPeriods.FiscalQuarter
        | undefined,
    [fiscalQuarters],
  );

  const currentFiscalYear = useMemo(
    () =>
      getCurrentFiscalEntity(fiscalYears) as
        | FixedPeriods.FiscalYear
        | undefined,
    [fiscalYears],
  );

  const allFiscals: FixedPeriods.AllFiscals = useMemo(
    () => ({
      fiscalQuarters,
      fiscalYears,
      fiscalPeriods,
    }),
    [fiscalQuarters, fiscalYears, fiscalPeriods],
  );

  return (
    <FiscalPeriodsContext.Provider
      value={{
        fiscalPeriods,
        fiscalQuarters,
        fiscalYears,
        currentFiscalPeriod,
        currentFiscalQuarter,
        currentFiscalYear,
        allFiscals,
      }}
    >
      {children}
    </FiscalPeriodsContext.Provider>
  );
};

export { FiscalPeriodsContext, FiscalPeriodsProvider };
